import { useState } from 'react'
import { ActionIcon, Badge, Button, Group, Modal, Select, Stack, TextInput, Title, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { DataTable } from 'mantine-datatable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconEdit, IconFileTypePdf, IconPaperclip, IconPlus, IconSearch } from '@tabler/icons-react'
import {
  ESTADOS, TIPOS_ORDEN, esEditableEstado,
  type Orden, type OrdenEntrada, type OrdenResumen,
} from '@serkat/shared'
import { ErrorApi, api, queryString } from '../../api/cliente'
import { useSesion } from '../auth/useSesion'
import { EstadoBadge } from '../../compartido/componentes/EstadoBadge'
import { PanelDocumento } from '../../compartido/componentes/PanelDocumento'
import { fecha, moneda, ruc as fmtRuc } from '../../compartido/formato'
import { FormularioOrden } from './componentes/FormularioOrden'

const POR_PAGINA = 15
interface Pagina { datos: OrdenResumen[]; total: number }

export function OrdenesPagina() {
  const { usuario } = useSesion()
  const qc = useQueryClient()
  const [pagina, setPagina] = useState(1)
  const [texto, setTexto] = useState('')
  const [buscar] = useDebouncedValue(texto, 300)
  const [estado, setEstado] = useState<string | null>(null)
  const [tipo, setTipo] = useState<string | null>(null)
  const [editando, setEditando] = useState<Orden | undefined>()
  const [abierto, modal] = useDisclosure(false)
  const [panel, setPanel] = useState<OrdenResumen | null>(null)

  const filtro = {
    pagina, porPagina: POR_PAGINA,
    buscar: buscar || undefined, estado: estado || undefined, tipo: tipo || undefined,
  }

  const { data, isFetching } = useQuery({
    queryKey: ['ordenes', filtro],
    queryFn: () => api.get<Pagina>(`/ordenes${queryString(filtro)}`),
    placeholderData: (previo) => previo,
  })

  const guardar = useMutation({
    mutationFn: (v: OrdenEntrada) =>
      editando ? api.patch(`/ordenes/${editando.id}`, v) : api.post('/ordenes', v),
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Orden guardada' })
      void qc.invalidateQueries({ queryKey: ['ordenes'] })
      modal.close()
    },
    onError: (e) =>
      notifications.show({
        color: 'red', title: 'No se pudo guardar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  const puedeEmitir = usuario?.puedeEmitir ?? false

  async function abrir(fila?: OrdenResumen) {
    setEditando(fila ? await api.get<Orden>(`/ordenes/${fila.id}`) : undefined)
    modal.open()
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Órdenes de compra y servicio</Title>
        {puedeEmitir && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => abrir()}>
            Nueva orden
          </Button>
        )}
      </Group>

      <Group align="flex-end">
        <TextInput
          placeholder="Buscar por número, proveedor o RUC"
          leftSection={<IconSearch size={16} />}
          value={texto}
          onChange={(e) => { setTexto(e.currentTarget.value); setPagina(1) }}
          w={340}
        />
        <Select placeholder="Compra y servicio" clearable w={190}
          data={TIPOS_ORDEN.map((t) => ({ value: t, label: t === 'COMPRA' ? 'Compra' : 'Servicio' }))}
          value={tipo} onChange={(v) => { setTipo(v); setPagina(1) }} />
        <Select placeholder="Todos los estados" clearable w={180}
          data={ESTADOS.map((e) => ({ value: e, label: e }))}
          value={estado} onChange={(v) => { setEstado(v); setPagina(1) }} />
      </Group>

      <DataTable
        withTableBorder borderRadius="sm" striped highlightOnHover minHeight={220}
        fetching={isFetching}
        records={data?.datos ?? []}
        totalRecords={data?.total ?? 0}
        recordsPerPage={POR_PAGINA}
        page={pagina}
        onPageChange={setPagina}
        noRecordsText="No hay órdenes emitidas"
        idAccessor="id"
        columns={[
          {
            accessor: 'numero', title: 'Número', width: 150,
            render: (o) => <span style={{ fontFamily: 'monospace' }}>{o.numero}</span>,
          },
          {
            accessor: 'tipo', title: 'Tipo', width: 100,
            render: (o) => (
              <Badge variant="light" size="sm" color={o.tipo === 'COMPRA' ? 'indigo' : 'grape'}>
                {o.tipo === 'COMPRA' ? 'Compra' : 'Servicio'}
              </Badge>
            ),
          },
          { accessor: 'proveedor', title: 'Proveedor', width: '26%' },
          {
            accessor: 'proveedorRuc', title: 'RUC', width: 130,
            render: (o) => <span style={{ fontFamily: 'monospace' }}>{fmtRuc(o.proveedorRuc)}</span>,
          },
          { accessor: 'fechaEmision', title: 'Emisión', width: 115, render: (o) => fecha(o.fechaEmision) },
          {
            accessor: 'total', title: 'Total', width: 140, textAlign: 'right',
            render: (o) => (
              <span style={{ fontFamily: 'monospace' }}>
                {moneda(o.total, o.moneda as 'PEN' | 'USD')}
              </span>
            ),
          },
          {
            accessor: 'requerimientoNumero', title: 'Requerimiento', width: 150,
            render: (o) => o.requerimientoNumero ?? '—',
          },
          { accessor: 'estado', title: 'Estado', width: 115, render: (o) => <EstadoBadge estado={o.estado} /> },
          {
            accessor: 'acciones', title: '', width: 124,
            render: (o) => (
              <Group gap={2} wrap="nowrap">
                <Tooltip label="Descargar en formato oficial">
                  <ActionIcon
                    component="a"
                    href={`/api/ordenes/${o.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    variant="subtle"
                  >
                    <IconFileTypePdf size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Adjuntos e historial">
                  <ActionIcon variant="subtle" onClick={() => setPanel(o)}>
                    <IconPaperclip size={16} />
                  </ActionIcon>
                </Tooltip>
                {puedeEmitir && esEditableEstado(o.estado) && (
                  <Tooltip label="Editar">
                    <ActionIcon variant="subtle" onClick={() => void abrir(o)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
      />

      <PanelDocumento
        documentoId={panel?.id ?? null}
        numero={panel?.numero ?? ''}
        estado={panel?.estado ?? 'Pendiente'}
        puedeEditar={puedeEmitir}
        onCerrar={() => setPanel(null)}
      />

      <Modal
        opened={abierto} onClose={modal.close}
        title={editando ? `Editar ${editando.numero}` : 'Nueva orden'}
        size="95rem"
      >
        <FormularioOrden
          inicial={editando}
          guardando={guardar.isPending}
          onGuardar={(v) => guardar.mutate(v)}
          onCancelar={modal.close}
        />
      </Modal>
    </Stack>
  )
}
